import { useMemo } from 'react'
import type { SourceId } from '../../../../Flux/Slice/Visualization/VisualizationSliceTypes.ts'
import { deepestMeaningfulTaxon } from '../Sinks/Selector/TaxaAggregationCore.ts'
import { useTransformer } from '../Transformer/UseTransformer.tsx'
import type { SinkTable } from './SinkTable.ts'
import { buildBreakdownTable, buildSeriesTable, countTaxaPerCategory } from './SinkTable.ts'
import { usePublishSinkTable } from './SinkTableContext.tsx'

/** What the numbers were produced under, so an exported CSV is not a set of anonymous figures. */
const caption = (opts: { selectedPath: string[]; gradeThreshold: number; valueLabel: string }): string => {
  const where = opts.selectedPath.length > 0 ? opts.selectedPath.join(' → ') : 'Root'
  const grade = opts.gradeThreshold > 0 ? ` · grade ≥ ${opts.gradeThreshold}%` : ''
  return `${where}${grade} · ${opts.valueLabel}`
}

type Common = {
  sourceId: SourceId
  bucketKey?: string
  selectedPath: string[]
  gradeThreshold: number
  /** Trait groups are terminal categories: nothing sits beneath one, so no parent and no n-taxa. */
  inTraitMode: boolean
  categoryLabel: string
  valueLabel: string
}

/**
 * The n-taxa counts for the categories at the current zoom level, and the taxon they sit under.
 *
 * `kronaFilteredBuckets` costs close to nothing on top of whatever the sink already asked for: it
 * shares the one memoised cube scan (`selectFilteredCube`) with every other transformer at the same
 * path and grade. Both are skipped in trait mode.
 */
function useTaxaContext(args: Common) {
  const { sourceId, bucketKey, selectedPath, gradeThreshold, inTraitMode } = args

  const bucketsRes = useTransformer(sourceId, bucketKey, 'kronaFilteredBuckets', { selectedPath, gradeThreshold })

  return useMemo(() => {
    if (inTraitMode) return { parentTaxon: undefined, taxaCounts: undefined }
    return {
      parentTaxon: selectedPath.length > 0 ? deepestMeaningfulTaxon(selectedPath) : undefined,
      taxaCounts:
        bucketsRes.status === 'ready' ? countTaxaPerCategory(bucketsRes.value, selectedPath.length, bucketKey) : undefined
    }
  }, [bucketsRes, inTraitMode, selectedPath, bucketKey])
}

/** Publishes one row per (group, category) — for the charts that stack a series across buckets. */
export function useSeriesSinkTable(
  args: Common & {
    ready: boolean
    categories: string[]
    rows: Array<{ label: string; sampleCount?: number; values: Record<string, number> }>
    groupLabel: string
  }
): void {
  const { parentTaxon, taxaCounts } = useTaxaContext(args)
  const { ready, categories, rows, groupLabel, categoryLabel, valueLabel, selectedPath, gradeThreshold } = args

  const table = useMemo<SinkTable | undefined>(() => {
    if (!ready) return undefined
    return buildSeriesTable({
      parentTaxon,
      categories,
      rows,
      taxaCounts,
      groupLabel,
      categoryLabel,
      valueLabel,
      caption: caption({ selectedPath, gradeThreshold, valueLabel })
    })
  }, [ready, parentTaxon, categories, rows, taxaCounts, groupLabel, categoryLabel, valueLabel, selectedPath, gradeThreshold])

  usePublishSinkTable(table)
}

/** Publishes one row per category — for the charts that break a single bucket down. */
export function useBreakdownSinkTable(
  args: Common & {
    ready: boolean
    entries: Array<{ name: string; value: number }>
    /** Off when the categories are not the taxa at the zoom level (a ranking by leaf or by rank). */
    withTaxaCounts?: boolean
  }
): void {
  const { parentTaxon, taxaCounts } = useTaxaContext(args)
  const { ready, entries, withTaxaCounts = true, categoryLabel, valueLabel, selectedPath, gradeThreshold } = args

  const table = useMemo<SinkTable | undefined>(() => {
    if (!ready) return undefined
    return buildBreakdownTable({
      parentTaxon,
      entries,
      taxaCounts: withTaxaCounts ? taxaCounts : undefined,
      categoryLabel,
      valueLabel,
      caption: caption({ selectedPath, gradeThreshold, valueLabel })
    })
  }, [ready, parentTaxon, entries, taxaCounts, withTaxaCounts, categoryLabel, valueLabel, selectedPath, gradeThreshold])

  usePublishSinkTable(table)
}
